import React, { useMemo } from 'react';
import { useParams } from 'react-router';
import {
  useNavigateWithBreadcrumbs,
  useSyncCurrentOrganization,
} from '../../hooks';
import { ROUTES } from '../../constants';
import i18n from '../../utilities/i18n';
import StudyPageHeader from './studyComponents/StudyHeader';
import { Site } from '../../models';
import {
  Button,
  Highlight,
  IconBannersWarning,
  LoadingSpinner,
  VStack,
} from '@adc/parallax-component-library';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  devicesByStudyQueryOptions,
  organizationQueryOptions,
  sitesQueryOptions,
  studyCapabilitiesQueryOptions,
  studyQueryOptions,
  subjectsQueryOptions,
} from '../../queryOptions';
import { useErrorMessageOnEntityView } from '../../hooks/useErrorMessageOnEntityView';
import { SitesTable } from './studyComponents/SitesTable';
import { SubjectsTable } from './studyComponents/SubjectsTable';
import { QrCodePopup } from '../qrCode/QrCodePopup';
import { DevicesTable } from '../devices/DevicesTable';
import { AssignDeviceModal } from '../deviceAssignment/AssignDeviceModal';
import { EntityType } from 'adc-sirius-module-common';

/**
 * Study component that displays the details of a specific study.
 * @returns {React.JSX.Element}
 */
export default function Study() {
  const navigateWithBreadcrumbs = useNavigateWithBreadcrumbs();

  const navigateToSite = (siteKey: string, siteLabel: string) => {
    navigateWithBreadcrumbs(
      ROUTES.SITES.DETAIL.replace(':siteKey', siteKey),
      siteLabel,
      study.data?.name || i18n.t('Page_Study.title'),
    );
  };

  const navigateToSubject = (subjectKey: string, subjectLabel: string) => {
    navigateWithBreadcrumbs(
      ROUTES.SUBJECTS.DETAIL.replace(':subjectKey', subjectKey),
      subjectLabel,
      study.data?.name || i18n.t('Page_Study.title'),
    );
  };

  const navigateToDevice = (deviceKey: string, appName: string) => {
    navigateWithBreadcrumbs(
      ROUTES.DEVICES.DETAIL.replace(':deviceKey', deviceKey),
      appName,
      study.data?.name || i18n.t('Page_Study.title'),
    );
  };

  const { studyKey } = useParams();

  const { data: capabilities } = useQuery(
    studyCapabilitiesQueryOptions({ key: studyKey }),
  );

  const [study, sites, devices] = useQueries({
    queries: [
      studyQueryOptions({ key: studyKey }),
      sitesQueryOptions(
        { studyKey },
        { enabled: !!studyKey && !!capabilities?.canGetSites },
      ),
      devicesByStudyQueryOptions(
        {
          key: studyKey,
        },
        { enabled: !!studyKey },
      ),
    ],
  });

  const errorMessage = useErrorMessageOnEntityView(
    study.error,
    'Page_Study.load_error',
  );

  const organization = useQuery(
    organizationQueryOptions(
      { key: study.data?.organizationKey },
      { enabled: !!study.data?.organizationKey },
    ),
  );

  const subjects = useQueries({
    queries: sites.isSuccess
      ? sites.data.map((site: Site) =>
          subjectsQueryOptions(
            { siteKey: site.key },
            { enabled: !!capabilities?.canGetSubjects },
          ),
        )
      : [],
    combine: (results) => {
      return {
        data: results
          .flatMap((result) => result.data)
          .filter((subject) => !!subject),
        pending: results.some((result) => result.isPending),
      };
    },
  });

  const qrCodeError = useMemo(() => {
    if (!organization.isPending && !organization.data) {
      return true;
    }

    if (!study.isPending && !study.data) {
      return true;
    }

    return false;
  }, [organization.isPending, organization.data, study.isPending, study.data]);

  useSyncCurrentOrganization(study.data?.organizationKey || '');

  return (
    <VStack gap="$4" height="100%">
      {study.isPending ? (
        <VStack alignItems="center" justifyContent="center" flexGrow={1}>
          <LoadingSpinner testID="study-loading" />
        </VStack>
      ) : null}

      {study.isError ? (
        <Highlight
          variant="danger"
          iconStart={
            <IconBannersWarning
              color="$neutral.0"
              accentColor="$support.danger.default"
            />
          }
          title={i18n.t('Page_Study.load_error_title')}
          body={i18n.t(errorMessage)}
          testID="study-error-banner"
        />
      ) : null}

      {study.isSuccess ? (
        <VStack gap="$4">
          <StudyPageHeader
            study={study.data}
            isReadonly={!capabilities?.canUpdateStudy}
          >
            <QrCodePopup
              organizationIdentifier={organization.data?.name || ''}
              organizationKey={organization.data?.key || ''}
              studyIdentifier={study.data?.name || ''}
              studyKey={study.data?.key || ''}
              error={qrCodeError}
              loading={study.isPending || organization.isPending}
            />
          </StudyPageHeader>
          {capabilities?.canGetSites ? (
            <SitesTable
              isLoading={sites.isPending}
              hasLoadingError={sites.isError}
              sites={sites.data || []}
              studyKey={study.data.key}
              navigateToSite={navigateToSite}
              canCreateSite={capabilities.canInsertSite}
            />
          ) : null}

          {capabilities?.canGetSubjects ? (
            <SubjectsTable
              isLoading={subjects.pending}
              hasLoadingError={false}
              subjects={subjects.data || []}
              sites={sites.data || []}
              navigateToSite={navigateToSite}
              navigateToSubject={navigateToSubject}
              canCreateSubject={capabilities.canInsertSubject}
            />
          ) : null}

          <DevicesTable
            title={i18n.t('Page_Study.table_devices_title')}
            description={i18n.t('Page_Study.table_devices_description')}
            isLoading={devices.isPending}
            hasLoadingError={devices.isError}
            devices={devices.data || []}
            navigateToDevice={navigateToDevice}
            toolbarRightContent={
              <AssignDeviceModal
                entityType={EntityType.Study}
                entityKey={study.data.key}
                entityName={study.data.name}
                trigger={
                  <Button
                    size="medium"
                    label={i18n.t('Page_Study.manage_assignments')}
                    onPress={() => {}}
                    disabled={
                      devices.isPending ||
                      study.isPending ||
                      !study.data?.organizationKey
                    }
                    testID="device-assignments-button"
                  />
                }
              />
            }
          />
        </VStack>
      ) : null}
    </VStack>
  );
}
